import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import { AppProvider, Page, Frame, DataTable, Card, Filters, ChoiceList, DatePicker, Stack, Spinner, Heading, Icon, Tooltip, Loading, Pagination, Link } from '@shopify/polaris';
import { InfoMinor } from '@shopify/polaris-icons';
import esTranslations from '@shopify/polaris/locales/es.json';

import axios from 'utils/axios';
import DateActionList from 'components/presentational/DateActionList';
import AutocompleteSearch from '../AutocompleteSearch';

import debounce from 'lodash/debounce';

const propTypes = {
  items: PropTypes.array,
  dateOptions: PropTypes.array.isRequired,
  defaultDateFilter: PropTypes.string,

  filterURL: PropTypes.string.isRequired,

  sellerOptions: PropTypes.array, // for admins
  userId: PropTypes.number,
  pagination: PropTypes.shape({
    count: PropTypes.number,
    last: PropTypes.number,
    next: PropTypes.number,
    prev: PropTypes.number,
    page: PropTypes.number,
  }).isRequired,

  variantAuditTrailURL: PropTypes.string.isRequired,
}

const defaultProps = {
  sellerOptions: [],
  defaultDateFilter: "date_today",
}

const KpiDashboard = (props) => {
  const buildItemsAsRows =  useCallback( ( zaItems ) => {
    return zaItems.map( (item) => {
      const auditURL = props.variantAuditTrailURL.replace("PRODUCT_ID", item.product_id).replace("VARIANT_ID", item.variant_id)
      return [
        <Link url={ auditURL } external >{ item.title }</Link>,
        item.sku,
        item.product_type,
        item.mpn,
        item.reposition,
        item.sales,
        item.stock,
        item.earnings,
        item.sales_velocity,
        item.sell_through
      ]
    });
  }, [props.variantAuditTrailURL])

  const csrfToken = document.querySelector("meta[name=csrf-token]").content;
  const firstUpdate = useRef(true);
  const [loading, setLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);
  const [items, setItems] = useState(buildItemsAsRows(props.items));
  const [filterState, setFilterState] = useState({ 
    dateFilter: props.defaultDateFilter,
    dateRange: {},
    sortBy: { field: "reposition", direction: "DESC" }, // index / direction
    userId: props.userId, // null if admin (admin must pick a seller)
  });
  const [pagination, setPagination] = useState(props.pagination);

  // csv
  // const downloadLinkRef = useRef(null);
  // const [fileBase64, setFileBase64] = useState(0);
  const exportFormRef = useRef(null);

  const submitFilter = async ( filterStateSnap, page = 1) => { // dateRange as object with { start: date, end: date}
    setLoading(true);
    try {
      const response = await axios.post(props.filterURL, { 
        date_enum: filterStateSnap.dateFilter, 
        ...filterStateSnap.dateRange, // start and end
        sort_by: filterStateSnap.sortBy,
        user_id: filterStateSnap.userId,
        page,
      });
      setItems(buildItemsAsRows(response.data.rows));
      setPagination(response.data.pagination);
    } catch (error) {
      console.log("E", error);
    }
    setLoading(false);
  }

  const debounceSubmitFilter = useCallback( debounce(submitFilter, 600), [])

  const handleDateChange = (value, _label, dates = {} ) => {
    setFilterState({ ...filterState, dateFilter: value, dateRange: dates })
  }

  const handleSort = (index, direction) => {
    setFilterState({ ...filterState, sortBy: { field: rowsInfo[index].key, direction: (direction == "descending") ? "DESC" : "ASC", } });
  };

  const handleSellerChange = (selectedOptions) => {
    setFilterState({ ...filterState, userId: selectedOptions[0], })
  }

  useEffect( () => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if(filterState.userId){ //userId must be selected / (this doesn't matter if this is seller view)
      setLoading(true);
      debounceSubmitFilter(filterState, 1); // When filter changes get back to page 1
    }
  }, [filterState]);

  // Callback for export (when changing formats as form is shared)
  useEffect( () => {
    if(exportFormat){
      setLoading(true);
      try {
        exportFormRef.current.submit();
      } catch (error) {
        console.log("E", error)
      }
      setLoading(false);
      setExportFormat(null);
    }
  }, [exportFormat]);

  const rowsInfo = [
    { key: "title", heading: I18n.t("activerecord.models.product.one"), sortable: false, type: "text" },
    { key: "sku", heading: I18n.t("activerecord.attributes.product.sku"), sortable: false, type: "text" },
    { key: "product_type", heading: I18n.t("activerecord.attributes.product.product_type"), sortable: false, type: "text" },
    { key: "mpn", heading: "MPN", sortable: false, type: "text"},
    { key: "reposition", heading: I18n.t("dashboard.kpi.reposition"), sortable: true, type: "text", tooltip: "Este número proyecta un inventario para 2 meses basado en el histórico del rango de fecha seleccionado. Su intención es funcionar como guía y no garantiza la venta de esas unidades. Te sugerimos reponer las unidades en positivo y retirar las unidades en negativo. Si ves un número de reposición muy alto, preguntános y te asesoramos." },
    { key: "sales", heading: "Ventas", sortable: true, type: "text" },
    { key: "stock", heading: "Inventario Actual", sortable: true, type: "text" },
    { key: "earnings", heading: "Ingresos + IVA", sortable: true, type: "text" },
    { key: "sales_velocity", heading: "Velocidad por mes", sortable: true, type: "text", tooltip: "Velocidad por mes es el numero de ventas promedio del producto en el mes cuando está en inventario. Se calcula dividiendo las unidades vendidas entre los días que esta en inventario el producto x 30 para el periodo seleccionado. El calculo excluye días que el producto no estuvo en inventario." },
    { key: "sell_through", heading: "% Vendido", sortable: true, type: "text", tooltip: "% Vendido (o Sell Through) es el porcentaje de unidades vendidas durante el periodo seleccionado. Se calcula dividiendo el numero de unidades vendidas entre el inventario inicial para el mismo periodo." },
  ];

  const controlsEnabled = !!filterState.userId;

  const exportCsv = () => setExportFormat("csv");
  const exportXlsx = () => setExportFormat("xlsx");

  // console.log("KPI", props)
  console.log("KPI filter state", filterState);

  const dataTableSortable = useMemo( () => rowsInfo.map( (ritem) => ritem.sortable ), []);
  const dataTableColumnContentTypes = useMemo( () => rowsInfo.map( (ritem) => ritem.type ), []);

  // Build custom headings with/without tooltips
  const dataTableHeadings = useMemo( () => {
    return rowsInfo.map( (ritem) => (
      <div className="flex">
        { ritem.tooltip &&
          <div className="mr5">
            <Tooltip content={ ritem.tooltip } dismissOnMouseOut>
              <Icon source={ InfoMinor } />
            </Tooltip>
          </div>
        }
        <span>
          { ritem.heading }
        </span>
      </div>
    ))
  }, []);

  return (
    <AppProvider i18n={esTranslations}>
      <Frame>
        <Page title="KPIs" fullWidth
          secondaryActions={[
            { content: "Exportar CSV", onAction: exportCsv, disabled: !controlsEnabled, loading: loading },
            { content: "Exportar XLSX", onAction: exportXlsx, disabled: !controlsEnabled, loading: loading },
          ]}
        >
          { loading && <Loading /> }
          <Card>
            <Card.Section>
              <Stack spacing="tight" >
                <Stack.Item fill>
                  { props.sellerOptions &&
                    <AutocompleteSearch
                      placeholder = "Buscar por Seller..."
                      filterOptions = { props.sellerOptions }
                      searchDelay={ 300 }
                      onSelectionCallback={ handleSellerChange }
                    />
                  }
                </Stack.Item>
                <Stack.Item>
                  <DateActionList
                    loading={ loading }
                    disabled={ !controlsEnabled }
                    onChange={ handleDateChange }
                    options={ props.dateOptions }
                    defaultDateOptionValue={ props.defaultDateFilter }
                  />
                </Stack.Item>
              </Stack>
            </Card.Section>
            <div className="kpi-datatable">
              <DataTable
                rows={ items }
                headings={ dataTableHeadings }
                defaultSortDirection="descending"
                sortable={ dataTableSortable }
                onSort={ handleSort }
                columnContentTypes={ dataTableColumnContentTypes }
                initialSortColumnIndex={ 3 }
              />
            </div>
          </Card>

          <div className="flex justifyCenter mt20">
            <Pagination
              hasPrevious={ !!pagination.prev }
              hasNext={ !!pagination.next }
              onPrevious={ () => submitFilter(filterState, pagination.page - 1) }
              onNext={ () => submitFilter(filterState, pagination.page + 1) }
            />
          </div>
          {/* EXPORT Form */}
          <form className="hidden_imp" method="POST" action={ `${props.filterURL}.${exportFormat}` } ref={ exportFormRef } target="_blank">
            <input type="hidden" name="date_enum" value={ filterState.dateFilter } />
            <input type="hidden" name="sort_by[field]" value={filterState.sortBy.field || undefined} />
            <input type="hidden" name="sort_by[direction]" value={filterState.sortBy.direction || undefined} />
            <input type="hidden" name="start" value={filterState.dateRange.start || undefined} />
            <input type="hidden" name="end" value={filterState.dateRange.end || undefined} />
            <input type="hidden" name="user_id" value={ filterState.userId || 0 } />
            <input type="hidden" name="authenticity_token" value={ csrfToken } />
          </form>
          {/* <a id="export-placeholder-link" className="hidden_i" ref={ downloadLinkRef } href={ fileBase64 } download={`kpi-csv.csv`}></a> */}
        </Page>
      </Frame>
    </AppProvider>
  )
};

KpiDashboard.propTypes = propTypes;
KpiDashboard.defaultProps = defaultProps;

export default KpiDashboard;